import { useEffect, useState } from "react";

import { documentAutomation } from "@smart/bridge-images-dom";
import {
  Button,
  Modal,
  RadioButtons,
  TextArea,
} from "@smart/itops-sb-design-system-dom";

type AutofillFeedbackProps = {
  open: boolean;
  title?: string;
  onSubmit?: (feedback?: string) => Promise<void>;
};

export const AutofillFeedback = ({
  open,
  title,
  onSubmit,
}: AutofillFeedbackProps) => {
  const [openModal, setOpenModal] = useState(open);
  const [accuracy, setAccuracy] = useState<string>();
  const [expectation, setExpectation] = useState<string>();
  const [other, setOther] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setOpenModal(open);
  }, [open]);

  const accuracyOptions = [
    {
      label: "Very accurately",
      value: "Filling my form by AI is very accurate",
    },
    {
      label: "Somewhat accurately",
      value: "Filling my form by AI is somewhat accurate",
    },
    {
      label: "Somewhat inaccurately",
      value: "Filling my form by AI is somewhat inaccurate",
    },
    {
      label: "Very inaccurately",
      value: "Filling my form by AI is very inaccurate",
    },
    {
      label: "I'm not sure",
      value: "I have no feedback on accuracy",
    },
  ];
  const expectationOptions = [
    {
      label: "Yes",
      value: "Autofill met my expectations",
    },
    {
      label: "No",
      value: "Autofill did not meet my expectations",
    },
    {
      label: "Somewhat",
      value: "Autofill somewhat met my expectations",
    },
    {
      label: "I had no expectations",
      value:
        "I had no expectations on how the autofill assistant fills out my form",
    },
  ];

  const submitFeedback = async () => {
    if (!onSubmit) return;

    const combinedFeedback = [accuracy, expectation, other].filter(
      (value) => !!value,
    );
    const feedbackToSubmit =
      combinedFeedback.length > 0 ? combinedFeedback.join(". ") : undefined;

    try {
      setSubmitting(true);
      await onSubmit(feedbackToSubmit);
    } catch (error) {
      console.error("Error submitting autofill feedback. ", error);
    } finally {
      setSubmitting(false);
      setOpenModal(false);
    }
  };

  const closeFeedback = async () => {
    if (onSubmit) {
      await onSubmit();
    }
    setOpenModal(false);
  };

  return (
    <Modal
      open={openModal}
      onClose={closeFeedback}
      size="default"
      loading={submitting}
      header={{
        text: title || "Autofill Assistant",
      }}
    >
      <div className="flex">
        <img
          src={documentAutomation}
          alt="Autofill feedback"
          className="m-4 h-[12rem] w-[12rem] flex-shrink-0 flex-grow-0 basis-[12rem]"
        />
        <div className="ml-4 flex-1">
          <div className="mr-8">
            <h3 className="mt-8 mb-4">
              How accurately did the Autofill Assistant fill out your form?
            </h3>
            <RadioButtons
              withLabelWrapper={false}
              options={accuracyOptions}
              value={accuracy || ""}
              onChange={(v) => setAccuracy(v)}
              name="autofill-accuracy"
              direction="column"
            />
            <h3 className="mt-8 mb-4">
              Did the Autofill Assistant meet your expectations?
            </h3>
            <RadioButtons
              withLabelWrapper={false}
              options={expectationOptions}
              value={expectation || ""}
              onChange={(v) => setExpectation(v)}
              name="autofill-expectation"
              direction="column"
            />
            <h3 className="mt-8 mb-4">Please provide any other feedback</h3>
            <TextArea
              value={other}
              onChange={(e) => setOther(e.target.value)}
              placeholder="Type your feedback so we can continue to improve your experience"
              className="mt-2"
            />
            <Button
              text="Submit feedback"
              type="button"
              name="submit"
              value="feeback"
              onClick={() => submitFeedback()}
              variant="primaryDefault"
              className="mt-8 mb-2"
              disabled={(!accuracy && !expectation && !other) || submitting}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};
